/**
 * The live "now" line on the timeline.
 *
 * Ticks once a minute rather than on an animation frame — the line only ever
 * moves in whole minutes, so anything faster is wasted renders. The interval
 * is re-aligned to the top of each minute after the first tick so the line
 * moves when the clock does, not 60s after mount.
 */
import { useEffect, useState } from "react";
import { minuteOfDay, formatMinute } from "@/lib/dates";
import { PX_PER_MIN } from "./constants";

export function useNowMinute(active: boolean): number {
  const [minute, setMinute] = useState(() => minuteOfDay());

  useEffect(() => {
    if (!active) return;

    setMinute(minuteOfDay());

    let interval: ReturnType<typeof setInterval> | undefined;
    const msToNextMinute =
      60_000 - (Date.now() % 60_000);

    const timeout = setTimeout(() => {
      setMinute(minuteOfDay());
      interval = setInterval(() => setMinute(minuteOfDay()), 60_000);
    }, msToNextMinute);

    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, [active]);

  return minute;
}

export function NowMarker({
  minute,
  startMinute,
}: {
  minute: number;
  startMinute: number;
}) {
  const top = (minute - startMinute) * PX_PER_MIN;

  return (
    <div
      className="absolute inset-x-0 z-20 pointer-events-none"
      style={{ top }}
      aria-hidden="true"
    >
      <div className="relative flex items-center">
        <div className="absolute -left-1 w-2 h-2 rounded-full bg-destructive animate-pulse-ring" />
        <div className="flex-1 h-px bg-destructive/70" />
        <span className="ml-2 text-[10px] font-mono text-destructive tabular-nums">
          {formatMinute(minute)}
        </span>
      </div>
    </div>
  );
}
